<template lang="pug">
.map-container(ref="container" :class="{ show, edit }")
    .map(ref="map")
        el-button.play-animation(
            v-if="sectionSettingValues && sectionSettingValues.animate && show"
            type="info"
            size="mini"
            icon="el-icon-fa-play fas"
            @click.stop.prevent="playAnimation"
        ) {{ $t('Play animation') }}
        MglMap(
            ref="mapbox"
            :access-token="accessToken"
            :map-style="style"
            :center="[10.4515, 51.1657]"
            :zoom="3"
            :pitch="40"
            :bearing="-20"
            :interactive="false"
            @load="mapBoxLoaded"
        )
            Pin(
                v-for="marker in markers" :key="marker.id"
                :marker="marker"
            )
</template>

<i18n>
{
    "de": {
        "Marker": "Pin",
        "Play animation": "Animation abspielen"
    }
}
</i18n>

<script>
import _ from 'lodash'
import Mapbox from 'mapbox-gl'
import MultiTouch from 'mapbox-gl-multitouch'
import { MglMap } from 'vue-mapbox'

import InputContents from '@/components/Page/Sections/Partials/InputContents'
import MarkerContent from './MarkerContent'
import Pin from './Pin'

export default {
    name: 'Map',
    extends: InputContents,
    data() {
        // let style = 'mapbox://styles/vanameland/ckclxo9az13jn1io5gnwr9tjr'
        let style = 'mapbox://styles/vanameland/ckirockk106341ao7dv35bc1p'
        if (this.sectionSettingValues && this.sectionSettingValues.style) {
            if (this.sectionSettingValues.style.startsWith('mapbox://')) {
                style = this.sectionSettingValues.style
            } else if (this.sectionSettingValues.style === 'custom' && this.sectionSettingValues['mapbox-style-url'].startsWith('mapbox://')) {
                style = this.sectionSettingValues['mapbox-style-url']
            }
        }

        return {
            accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            style,
            // center: [10.4515, 51.1657], // germany
            show: false,
        }
    },
    components: {
        MglMap,
        Pin
    },
    watch: {
        edit() {
            if (this.$refs.mapbox) {
                this.$nextTick(() => {
                    this.rerenderMap()
                    if (this.$refs.mapbox.map) this.$refs.mapbox.map.resize()
                })
            }
        },
        contents() {
            if (this.$refs.mapbox) {
                this.$nextTick(() => {
                    this.rerenderMap()
                    window.dispatchEvent(new Event('scroll'));
                    if (this.$refs.mapbox.map) this.$refs.mapbox.map.resize()
                })
            }
        },
        markers: {
            deep: true,
            handler() {
                if (this.$refs.mapbox) {
                    this.$nextTick(() => {
                        this.rerenderMap()
                        if (this.$refs.mapbox.map) this.$refs.mapbox.map.resize()
                    })
                }
            }
        },
        sectionSettingValues: {
            deep: true,
            handler() {
                if (this.$refs.mapbox) {
                    this.$nextTick(() => {
                        // this.$refs.mapbox.map.resize()
                        this.rerenderMap()
                    })
                }
            }
        }
    },
    computed: {
        markers() {
            let markers = []

            const contents = this.contents || []
            contents.filter(el => el.key === 'marker' && ! el.deleted).forEach(content => {
                const address = _.get(content, 'addresses.0')
                if (address && ! address.deleted && address.latitude && address.longitude) {
                    markers.push({
                        id: content.id,
                        coordinates: [address.longitude, address.latitude],
                        color: content.color,
                        title: content.title,
                        char: content.sub_title,
                        description: content.text,
                    })
                }
            })

            return markers
        },
    },
    methods: {
        rerenderMap() {
            if (this.$refs.mapbox && this.$refs.mapbox.map && this.sectionSettingValues) {
                // if (this.edit) {
                //     this.$refs.mapbox.map.boxZoom.enable();
                //     this.$refs.mapbox.map.scrollZoom.enable();
                //     this.$refs.mapbox.map.dragPan.enable();
                //     this.$refs.mapbox.map.dragRotate.enable();
                //     this.$refs.mapbox.map.keyboard.enable();
                //     this.$refs.mapbox.map.doubleClickZoom.enable();
                //     this.$refs.mapbox.map.touchZoomRotate.enable();
                // } else {
                //     this.$refs.mapbox.map.boxZoom.disable();
                //     this.$refs.mapbox.map.scrollZoom.disable();
                //     this.$refs.mapbox.map.dragPan.disable();
                //     this.$refs.mapbox.map.dragRotate.disable();
                //     this.$refs.mapbox.map.keyboard.disable();
                //     this.$refs.mapbox.map.doubleClickZoom.disable();
                //     this.$refs.mapbox.map.touchZoomRotate.disable();
                // }

                this.$refs.mapbox.map.resize()

                let style
                if (this.sectionSettingValues.style) {
                    if (this.sectionSettingValues.style.startsWith('mapbox://')) {
                        style = this.sectionSettingValues.style
                    } else if (this.sectionSettingValues.style === 'custom' && this.sectionSettingValues['mapbox-style-url'].startsWith('mapbox://')) {
                        style = this.sectionSettingValues['mapbox-style-url']
                    }
                }

                if (style && style !== this.style) {
                    this.style = style
                    this.$refs.mapbox.map.setStyle(style);
                }

                let zoom = 3.5
                if (this.sectionSettingValues['auto-zoom-by-markers'] === false) {
                    zoom = parseFloat(this.sectionSettingValues.zoom) || 0
                    // this.$refs.mapbox.map.setZoom(zoom);
                } else if (this.markers && this.markers.length) {
                    let bounds = new Mapbox.LngLatBounds()
                    this.markers.forEach(marker => bounds.extend(marker.coordinates))
                    let padding = 40
                    if (this.sectionSettingValues['animate'] &&
                        this.sectionSettingValues['animation-auto-zoom-by-markers']) {
                        padding = 80
                    }
                    const options = {
                        padding,
                        maxZoom: 10,
                        duration: 0
                    }
                    this.$refs.mapbox.map.fitBounds(bounds, options)
                    zoom = this.$refs.mapbox.map.getZoom()
                }

                let center = [10.4515, 51.1657] // germany
                if (this.sectionSettingValues
                    && this.sectionSettingValues['auto-center-by-markers'] === false
                    && this.sectionSettingValues.latitude !== undefined
                    && this.sectionSettingValues.longitude !== undefined
                ) {
                    center = [this.sectionSettingValues.longitude, this.sectionSettingValues.latitude]
                } else if (this.markers
                    && this.markers.length
                    && this.sectionSettingValues['auto-center-by-markers']
                ) {
                    let bounds = new Mapbox.LngLatBounds()
                    this.markers.forEach(marker => bounds.extend(marker.coordinates))
                    center = bounds.getCenter()
                }
                this.$refs.mapbox.map.setCenter(center);

                const pitch = parseFloat(this.sectionSettingValues.pitch) || 0
                // this.$refs.mapbox.map.setPitch(pitch);

                const bearing = parseFloat(this.sectionSettingValues.bearing) || 0
                // this.$refs.mapbox.map.setBearing(bearing);

                let projection = 'mercator'
                if (_.get(this.sectionSettingValues, 'globe-projection')) projection = 'globe'
                this.$refs.mapbox.map.setProjection(projection)

                if (projection = 'globe' && _.get(this.sectionSettingValues, 'globe-fog') !== false) {
                    this.$refs.mapbox.map.setFog({});
                } else {
                    console.log('unset fog')
                    this.$refs.mapbox.map.setFog(null);
                }

                const options = {
                    center,
                    essential: true,
                    bearing,
                    pitch,
                    zoom,
                    duration: 0,
                }
                this.$refs.mapbox.map.flyTo(options)
            }
        },
        playAnimation() {
            this.rerenderMap()

            setTimeout(() => {
                if (this.$refs.mapbox && this.$refs.mapbox.map
                    && this.sectionSettingValues && this.sectionSettingValues.animate
                ) {

                    const duration = Math.max(parseInt(this.sectionSettingValues.duration)) || 300

                    let zoom = 3.5
                    if (this.sectionSettingValues['animation-auto-zoom-by-markers'] === false) {
                        zoom = parseFloat(this.sectionSettingValues['animation-zoom']) || 0
                    }

                    const pitch = parseFloat(this.sectionSettingValues['animation-pitch']) || 0
                    const bearing = parseFloat(this.sectionSettingValues['animation-bearing']) || 0

                        // if auto center AND auto zoom
                        // fit bounds
                        // otherwise fly to

                    if (this.markers
                        && this.markers.length
                        && this.sectionSettingValues['animation-auto-center-by-markers']
                        && this.sectionSettingValues['animation-auto-zoom-by-markers']
                    ) {
                        let bounds = new Mapbox.LngLatBounds()
                        this.markers.forEach(marker => bounds.extend(marker.coordinates))

                        // console.log('bounds', bounds, bounds.getCenter());

                        let options = {
                            padding: 40,
                            bearing,
                            pitch,
                            maxZoom: 10,
                            duration,
                        }

                        let map = this.$refs.mapbox.map
                        map.fitBounds(bounds, options)
                    } else {
                        let maxZoom
                        if (this.sectionSettingValues['animation-auto-zoom-by-markers'] === true) maxZoom = 10

                        let center = [10.4515, 51.1657] // germany
                        if (this.sectionSettingValues['animation-auto-center-by-markers'] === false
                            && this.sectionSettingValues['animation-latitude'] !== undefined
                            && this.sectionSettingValues['animation-longitude'] !== undefined
                        ) {
                            center = [this.sectionSettingValues['animation-longitude'], this.sectionSettingValues['animation-latitude']]
                        } else if (this.markers
                            && this.markers.length
                            && this.sectionSettingValues['animation-auto-center-by-markers']
                        ) {
                            let bounds = new Mapbox.LngLatBounds()
                            this.markers.forEach(marker => bounds.extend(marker.coordinates))
                            center = bounds.getCenter()
                        }

                        let options = {
                            center,
                            essential: true,
                            bearing,
                            pitch,
                            zoom,
                            maxZoom,
                            duration,
                        }

                        this.$refs.mapbox.map.flyTo(options)
                    }
                }
            }, 300)
        },
        async mapBoxLoaded(event) {

            this.$nextTick(() => {
                this.show = true
                this.$nextTick(this.rerenderMap)
            });

            let map = event.map
            map.addControl(new MultiTouch());
        }
    }
}
</script>

<style lang="sass" scoped>
$border-radius: 4px

.map-container

    --map-min-height: 245px
    &.edit
        position: sticky
        top: 0
        z-index: 1
        --map-min-height: 350px

button.play-animation
    position: absolute
    z-index: 1
    top: 0.5rem
    left: 0.5rem

.map-container
    position: relative
    // margin-bottom: .5rem
    .map
        // background: rgba(255, 255, 255, .4)
        background: $color-skeleton
        // border-radius: $border-radius
        overflow: hidden
        width: 100%
        height: 100%
        min-height: var(--map-min-height)
        // max-height: calc(100vh - 2rem)
        max-height: 40vh
    .mgl-map-wrapper
        opacity: 0
        transition: opacity .3s ease
        width: 100%
        height: 100%
        min-height: var(--map-min-height)
        // max-height: calc(100vh - 2rem)
        max-height: 40vh
        // max-height: 600px
        ::v-deep
            .mapboxgl-canvas-container
                height: 100%
                width: 100%
    &.show
        .mgl-map-wrapper
            opacity: 1
    .title
        position: absolute
        left: 0
        bottom: 0
        margin: 0
        width: 100%
        padding: 6px 12px
        background: rgba(255, 255, 255, .6)
        backdrop-filter: blur(2px)
        z-index: 1
        *
            margin: 0
            padding: 0
            font-size: .75em
</style>
